import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Trophy, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkoutSetData } from '@/types';

interface CompactSetTableProps {
  sets: WorkoutSetData[];
  onDeleteSet: (setId: string) => void;
  isDeleting: boolean;
  /** Edellisen treenin paras paino — jos nykyinen sarja ylittää, korostetaan */
  previousBestWeight?: number | null;
}

export function CompactSetTable({ sets, onDeleteSet, isDeleting, previousBestWeight }: CompactSetTableProps) {
  // Sarja, jonka poistoa varmistetaan (null = ei modaalia)
  const [pendingDelete, setPendingDelete] = useState<{ id: string; index: number } | null>(null);

  if (sets.length === 0) {
    return (
      <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">
        Ei vielä sarjoja. Kirjaa ensimmäinen sarja alta.
      </p>
    );
  }

  const confirmDelete = () => {
    if (pendingDelete) {
      onDeleteSet(pendingDelete.id);
      setPendingDelete(null);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-[2.5rem_1fr_1fr_2.5rem] gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
        <span>#</span>
        <span>Paino</span>
        <span>Toistot</span>
        <span />
      </div>

      {/* Rows */}
      {sets.map((set, index) => {
        const isNewRecord =
          previousBestWeight != null &&
          set.weight !== null &&
          Number(set.weight) > previousBestWeight;

        return (
          <div
            key={set.id || index}
            className={cn(
              'grid grid-cols-[2.5rem_1fr_1fr_2.5rem] gap-1 px-3 py-2.5 items-center',
              'text-sm',
              isNewRecord
                ? 'bg-yellow-50 dark:bg-yellow-900/10'
                : index % 2 === 0
                  ? 'bg-white dark:bg-gray-900'
                  : 'bg-gray-50 dark:bg-gray-800/50'
            )}
          >
            <span className="text-gray-400 dark:text-gray-500 font-medium">
              {index + 1}
            </span>
            <span className={cn(
              'font-medium',
              isNewRecord
                ? 'text-yellow-700 dark:text-yellow-400 flex items-center gap-1'
                : 'text-gray-900 dark:text-white'
            )}>
              {set.weight !== null ? `${set.weight} kg` : '-'}
              {isNewRecord && <Trophy className="h-3.5 w-3.5 inline-block" />}
            </span>
            <span className="font-medium text-gray-900 dark:text-white">
              {set.reps}
            </span>
            <button
              type="button"
              onClick={() => {
                if (set.id) setPendingDelete({ id: set.id, index });
              }}
              disabled={isDeleting || !set.id}
              className={cn(
                'p-1 rounded-sm text-gray-300 dark:text-gray-600',
                'transition-colors touch-manipulation',
                'hover:text-red-500 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-900/20',
                'active:text-red-600',
                'disabled:opacity-30 disabled:pointer-events-none'
              )}
              aria-label={`Poista sarja ${index + 1}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}

      {/* Poiston varmistus -modaali */}
      <Transition appear show={pendingDelete !== null} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setPendingDelete(null)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-xs" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="card w-full max-w-xs p-5 text-center">
                <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                  <AlertTriangle className="h-5 w-5" aria-hidden="true" />
                </div>
                <Dialog.Title className="text-base font-semibold text-gray-900 dark:text-white">
                  Poistetaanko sarja?
                </Dialog.Title>
                {pendingDelete && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Sarja {pendingDelete.index + 1} poistetaan pysyvästi.
                  </p>
                )}
                <div className="mt-5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPendingDelete(null)}
                    className="btn btn-secondary flex-1"
                  >
                    Peruuta
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    disabled={isDeleting}
                    className="btn btn-danger flex-1"
                  >
                    Poista
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
